import CardGridView from "@/components/CardGridView";
import { areaCreateUrlBuilder, areaIndexUrlBuilder } from "@/lib/url-builder";
import { Area, AreaService } from "@/services/AreaService";
import { Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { useOutletContext, useParams } from "react-router";
import { useSiteName } from "@/hooks/useSiteName";
import type { LayoutContext } from "@/lib/models";
import DeleteDialog from "@/components/DeleteDialog";
import { Loading } from "@/components/Loading";

const AreaListPage: React.FC = () => {
  const params = useParams();
  const { siteName } = useSiteName(params.siteId);
  const { setPageName } = useOutletContext<LayoutContext>();
  const [showDeleteFeatureDialog, setShowDeleteFeatureDialog] = useState(false);
  const [areas, setAreas] = useState<Array<Area>>([]);
  const [deletedAreaId, setDeletedAreaId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPageName(`Site: ${siteName} Areas`);
  }, [siteName, setPageName]);
  

  useEffect(() => {
    AreaService
    .getBySiteId(params.siteId!).then((areas) => {
      setAreas(areas);
    })
    .finally(() => setLoading(false));
  }, [params.siteId]);

  return (
    <>
      <div className="container mx-auto p-4">
        <Loading message="Loading areas..." isLoading={loading} />
        <CardGridView
          title={`Site: ${siteName}`}
          editPageUrl={(site: string, id: string) => areaIndexUrlBuilder(params.tenantId!, site, id)}
          createPageUrl={areaCreateUrlBuilder(params.tenantId!, params.siteId!)}
          items={areas}
          onDeleteItem={async (area) => {
            setDeletedAreaId(area.id)
            setShowDeleteFeatureDialog(true);
          }}
          renderContentItem={(item) => (
            <div className="space-y-1">
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-2">{item.name}</div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">{item.description}</div>
                {/* <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs">
                    <Users className="h-3 w-3 mr-1" />
                    Assigned Name
                  </Badge>
                </div> */}
              </div>
            </div>
          )}
          tableHeaders={[
            { id: "name", child: <>Name</> },
            { id: "description", child: <>Description</> },
            { id: "actions", child: "Edit", },
          ]}
          tableContentItem={(item) => [
            <span key="name">{item.name}</span>,
            <span key="description">{item.description}</span>,
            <Pencil key="actions" className="h-4 w-4" aria-label={`Edit Area`} />,
          ]}
        />
      </div>
      <DeleteDialog 
        onDelete={async () => {
          if (!deletedAreaId) {
            alert("No area selected for deletion");
            return;
          }
          await AreaService.delete(deletedAreaId)
          setAreas(areas.filter(a => a.id !== deletedAreaId))
          setDeletedAreaId(null)
        }} 
        deleteConfirmationText={<p>Are you sure you want to delete this area?</p>} 
        isOpen={showDeleteFeatureDialog}
        onOpenChange={setShowDeleteFeatureDialog}
      />
    </>
  );
};

export default AreaListPage;
